// Invitation redemption (Core v2.5 §6.1, unchanged in v2.6). The token lives only in this module's memory and in the
// JSON body of the two public POSTs; it never reaches the URL, storage, caches, logs or error reports.
import { own, isTestAddress } from "./memberDerive";

export const INVITE_PATH = "/einladung";
export const TOKEN_MAX = 128;
export const EMAIL_MAX = 200;
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 200;

export const INVITE_TEXT = {
  invalid: "Dieser Einladungslink ist ungültig, abgelaufen oder bereits verwendet. Bitte wende dich an die Person, die dich eingeladen hat.",
  rate: "Zu viele Versuche. Bitte später erneut versuchen.",
  config: "Konfigurationsfehler: Diese App-Adresse ist für Einladungen nicht freigegeben. Bitte melde das dem VISIBILIS-Team.",
  connection: "Die Einladung konnte nicht geprüft werden, weil das VISIBILIS-System nicht antwortet. Bitte versuche es erneut.",
  created: "Konto angelegt. Die Anmeldung ist nach der Profilprüfung möglich.",
  input: "Bitte prüfe deine Eingaben: E-Mail höchstens 200 Zeichen, Passwort 10 bis 200 Zeichen.",
  password: "Das Passwort erfüllt die Passwortregeln nicht.",
  unclear: "Die Verbindung ist unterbrochen. Ob dein Konto angelegt wurde, ist nicht bestätigt. Sende erneut – ist es bereits angelegt, meldet die Seite den Link als verwendet.",
};

let token = null;
let flight = null;

const cleanToken = (v) => (typeof v === "string" && v && !/[\s\u0000-\u001f\u007f]/.test(v) ? v : null);

function fragmentToken(hash) {
  const m = /(?:^|&)token=([^&]*)/.exec(String(hash || "").replace(/^#/, ""));
  if (!m) return null;
  try { return cleanToken(decodeURIComponent(m[1])); } catch (_) { return null; }
}

// Runs once before the router starts: on /einladung the fragment is read, then removed from the address bar
// (a token in the query is never used, only removed). The page also sends no referrer.
export function captureInviteToken(loc, hist, doc) {
  if ((String(loc.pathname || "").replace(/\/+$/, "") || "/") !== INVITE_PATH) return false;
  token = fragmentToken(loc.hash);
  flight = null;
  if (loc.hash || loc.search) hist.replaceState(null, "", INVITE_PATH);
  if (doc && !doc.querySelector('meta[name="referrer"]')) {
    const meta = doc.createElement("meta");
    meta.setAttribute("name", "referrer");
    meta.setAttribute("content", "no-referrer");
    doc.head.appendChild(meta);
  }
  return true;
}

export const inviteToken = () => token;
export const hasUsableToken = () => Boolean(token) && Array.from(token).length <= TOKEN_MAX;
export function forgetInviteToken() { token = null; flight = null; }
export function resetInviteCheck() { flight = null; }

const UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?(?:Z|\+00:00)$/;
const validUtc = (v) => typeof v === "string" && UTC.test(v) && !Number.isNaN(Date.parse(v));

function common(status) {
  if (status === 410 || status === 422) return { kind: "invalid", text: INVITE_TEXT.invalid };
  if (status === 429) return { kind: "rate", text: INVITE_TEXT.rate };
  if (status === 403) return { kind: "config", text: INVITE_TEXT.config };
  return { kind: "connection", text: INVITE_TEXT.connection };
}

// POST /member/invitations/check → only {valid: true, first_name, expires_at (UTC)} opens the form.
export function checkOutcome({ status, data } = {}) {
  if (status === 200) {
    const name = own(data, "first_name");
    return own(data, "valid") === true && typeof name === "string" && name.trim() && validUtc(own(data, "expires_at"))
      ? { kind: "valid", firstName: name.trim(), expiresAt: data.expires_at }
      : { kind: "connection", text: INVITE_TEXT.connection };
  }
  return common(status);
}

// One check per token, shared by repeated effect runs; without a usable token nothing is sent.
export function checkInvitation(post) {
  if (!hasUsableToken()) return Promise.resolve({ kind: "invalid", text: INVITE_TEXT.invalid });
  if (!flight || flight.token !== token) flight = { token, promise: post("/member/invitations/check", { token }).then(checkOutcome) };
  return flight.promise;
}

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const cpLen = (v) => Array.from(String(v ?? "")).length;

export function redeemErrors({ email, password }, testOnly = false) {
  const e = {};
  const mail = String(email ?? "").trim();
  if (!mail) e.email = "Bitte gib die E-Mail-Adresse ein, an die die Einladung ging.";
  else if (cpLen(mail) > EMAIL_MAX || !isEmail(mail)) e.email = "Bitte gib eine gültige E-Mail-Adresse mit höchstens 200 Zeichen ein.";
  else if (testOnly && !isTestAddress(mail)) e.email = "Im lokalen Teststack sind nur fiktive Testadressen erlaubt (z. B. @example.com).";
  const n = cpLen(password);
  if (n < PASSWORD_MIN || n > PASSWORD_MAX) e.password = "Das Passwort muss 10 bis 200 Zeichen lang sein.";
  return e;
}

export const redeemBody = ({ email, password }) => ({ token, email: String(email ?? "").trim(), password: String(password ?? "") });

// Server rule text for 400 at the password field: a short plain string only.
const ruleText = (d) => {
  const v = own(d, "detail");
  return typeof v === "string" && v.trim() && cpLen(v) <= 300 && !/[\u0000-\u001f\u007f<>]/.test(v) ? v.trim() : INVITE_TEXT.password;
};

// POST /member/invitations/redeem: only 201 {ok: true, status: "ausstehend"} counts as created; no login follows.
export function redeemOutcome({ status, data } = {}) {
  if (status === 201) return own(data, "ok") === true && own(data, "status") === "ausstehend" ? { kind: "created", text: INVITE_TEXT.created } : { kind: "unclear", text: INVITE_TEXT.unclear };
  if (status === 400) return { kind: "password", text: ruleText(data) };
  if (status === 422) return { kind: "input", text: INVITE_TEXT.input };
  if (status === 410 || status === 429 || status === 403) return common(status);
  return { kind: "unclear", text: INVITE_TEXT.unclear };
}
